import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { getRfqDashboardSummary } from "../../../api/Estimates/estimateRfqApi";
import {
  dashCountChipSx,
  dashIconWellSx,
  getDashAccent,
} from "../myDashboardStyles";

const BUCKETS = [
  {
    key: "pedidas",
    labelKey: "myDashboard.rfqPedidas",
    defaultLabel: "Pedidas",
    short: "All",
    color: "#00897b",
    match: (i) => ["Sent", "Quoted", "Applied"].includes(i.estado),
  },
  {
    key: "pendientesContestar",
    labelKey: "myDashboard.rfqPendientesContestar",
    defaultLabel: "Pend. contestar",
    short: "Sent",
    color: "#0288d1",
    match: (i) => i.estado === "Sent",
  },
  {
    key: "contestadas",
    labelKey: "myDashboard.rfqContestadas",
    defaultLabel: "Contestadas",
    short: "OK",
    color: "#43a047",
    // Solo las ya metidas en escandallo (Applied). Quoted va en "Pend. asignar".
    match: (i) => i.estado === "Applied",
  },
  {
    key: "pendientesAsignar",
    labelKey: "myDashboard.rfqPendientesAsignar",
    defaultLabel: "Pend. asignar",
    short: "Apply",
    color: "#fb8c00",
    match: (i) => i.estado === "Quoted",
  },
];

const ESTADO_META = {
  Sent: { label: "Sent", color: "#0288d1" },
  Quoted: { label: "Quoted", color: "#fb8c00" },
  Applied: { label: "Applied", color: "#43a047" },
};

function fmtDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
  });
}

function groupByProveedor(items) {
  const map = new Map();
  for (const item of items) {
    const key =
      String(item.proveedorNombre || "").trim() ||
      (item.codProv != null ? `COD ${item.codProv}` : "Sin proveedor");
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  }
  return [...map.entries()].sort((a, b) =>
    a[0].localeCompare(b[0], undefined, { sensitivity: "base" }),
  );
}

function shortProveedor(name) {
  const s = String(name || "");
  if (s.length <= 42) return s;
  return `${s.slice(0, 40)}…`;
}

export default function RfqQuotesWidget({ editing }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const accent = getDashAccent(
    "widget-rfq-quotes",
    "widgets",
    theme.palette.mode,
  );
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [items, setItems] = useState([]);
  const [activeBucket, setActiveBucket] = useState("pendientesAsignar");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getRfqDashboardSummary();
        if (cancelled) return;
        setCounts(data.counts || {});
        setItems(data.items || []);
        const c = data.counts || {};
        if (c.pendientesAsignar > 0) setActiveBucket("pendientesAsignar");
        else if (c.pendientesContestar > 0)
          setActiveBucket("pendientesContestar");
        else if (c.contestadas > 0) setActiveBucket("contestadas");
        else setActiveBucket("pedidas");
      } catch {
        if (!cancelled) {
          setCounts({});
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const bucketDef = BUCKETS.find((b) => b.key === activeBucket) || BUCKETS[0];
  const filtered = useMemo(
    () => items.filter((i) => bucketDef.match(i)),
    [items, bucketDef],
  );
  const grouped = useMemo(() => groupByProveedor(filtered), [filtered]);

  const openEstimate = (item) => {
    if (editing) return;
    const id = item.idPresupuesto || item.presupuesto?.idPresupuesto;
    if (!id) return;
    navigate(`/estimates?idPresupuesto=${id}`);
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        p: 1.75,
        minHeight: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, mb: 1.35 }}>
        <Box
          sx={(th) => ({
            ...dashIconWellSx(th, accent),
            width: 36,
            height: 36,
            borderRadius: "12px",
          })}
        >
          <RequestQuoteOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Typography variant="subtitle2" fontWeight={800} letterSpacing={0.1}>
          {t("myDashboard.widgetRfqQuotes", {
            defaultValue: "Cotizaciones RFQ",
          })}
        </Typography>
        <Box sx={(th) => dashCountChipSx(th, accent)}>
          {counts.pedidas ?? 0}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <>
          {/* Filtros en una sola fila compacta */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.65,
              mb: 1.5,
              pb: 1.15,
              borderBottom: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.8),
            }}
          >
            {BUCKETS.map((b) => {
              const n = Number(counts[b.key]) || 0;
              const active = activeBucket === b.key;
              return (
                <Tooltip
                  key={b.key}
                  title={t(b.labelKey, { defaultValue: b.defaultLabel })}
                  arrow
                  enterDelay={400}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={() => !editing && setActiveBucket(b.key)}
                    sx={{
                      cursor: editing ? "default" : "pointer",
                      border: "1px solid",
                      borderColor: active
                        ? alpha(b.color, 0.55)
                        : alpha(theme.palette.divider, 0.85),
                      bgcolor: active
                        ? alpha(
                            b.color,
                            theme.palette.mode === "dark" ? 0.24 : 0.12,
                          )
                        : alpha(theme.palette.background.paper, 0.7),
                      color: active ? b.color : "text.secondary",
                      borderRadius: 999,
                      px: 1,
                      py: 0.4,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.6,
                      fontSize: "0.7rem",
                      fontWeight: 800,
                      lineHeight: 1.25,
                      fontFamily: "inherit",
                      transition: "all .15s ease",
                      "&:hover": editing
                        ? undefined
                        : {
                            borderColor: alpha(b.color, 0.65),
                            bgcolor: alpha(b.color, 0.1),
                          },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: b.color,
                        flexShrink: 0,
                      }}
                    />
                    {t(b.labelKey, { defaultValue: b.defaultLabel })}
                    <Box
                      component="span"
                      sx={{
                        minWidth: 17,
                        px: 0.45,
                        height: 16,
                        borderRadius: 999,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 10,
                        fontWeight: 800,
                        bgcolor: alpha(b.color, active ? 0.22 : 0.12),
                        color: b.color,
                      }}
                    >
                      {n}
                    </Box>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              minHeight: 0,
              pr: 0.35,
              mr: -0.25,
            }}
          >
            {!filtered.length ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: "block", py: 2, px: 0.5 }}
              >
                {t("myDashboard.rfqEmptyBucket", {
                  defaultValue: "No hay cotizaciones en este filtro.",
                })}
              </Typography>
            ) : (
              <Stack spacing={1.75}>
                {grouped.map(([proveedor, rows]) => (
                  <Box key={proveedor}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.85,
                        mb: 0.85,
                        px: 0.4,
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={800}
                        noWrap
                        title={proveedor}
                        sx={{
                          color: accent,
                          letterSpacing: 0.01,
                          maxWidth: "85%",
                          fontSize: "0.78rem",
                        }}
                      >
                        {shortProveedor(proveedor)}
                      </Typography>
                      <Box
                        sx={{
                          flex: 1,
                          height: 1,
                          bgcolor: alpha(accent, 0.18),
                          minWidth: 12,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.disabled",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                        }}
                      >
                        {rows.length}
                      </Typography>
                    </Box>

                    <Stack spacing={0.75}>
                      {rows.map((item) => {
                        const meta =
                          ESTADO_META[item.estado] || {
                            label: item.estado || "—",
                            color: theme.palette.text.secondary,
                          };
                        const num =
                          item.presupuesto?.numeroPresupuesto ||
                          `RFQ #${item.id}`;
                        const name =
                          item.presupuesto?.nombrePresupuesto || "—";
                        const deadline = fmtDate(item.deadline);
                        return (
                          <Box
                            key={item.id}
                            onClick={() => openEstimate(item)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              py: 1,
                              pl: 1.15,
                              pr: 0.55,
                              borderRadius: 2,
                              cursor: editing ? "default" : "pointer",
                              border: "1px solid",
                              borderColor: alpha(theme.palette.divider, 0.75),
                              borderLeft: `3px solid ${meta.color}`,
                              bgcolor: alpha(
                                theme.palette.background.paper,
                                0.9,
                              ),
                              transition:
                                "background-color .12s ease, transform .12s ease, border-color .12s ease",
                              "&:hover": editing
                                ? undefined
                                : {
                                    bgcolor: alpha(meta.color, 0.06),
                                    borderColor: alpha(meta.color, 0.35),
                                    transform: "translateX(2px)",
                                  },
                            }}
                          >
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  minWidth: 0,
                                  mb: 0.35,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={800}
                                  noWrap
                                  sx={{ fontSize: "0.86rem", lineHeight: 1.25 }}
                                >
                                  {num}
                                </Typography>
                                <Box
                                  sx={{
                                    px: 0.7,
                                    py: 0.15,
                                    borderRadius: 999,
                                    fontSize: "0.64rem",
                                    fontWeight: 800,
                                    letterSpacing: 0.02,
                                    color: meta.color,
                                    bgcolor: alpha(meta.color, 0.12),
                                    flexShrink: 0,
                                    lineHeight: 1.45,
                                  }}
                                >
                                  {meta.label}
                                </Box>
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                sx={{
                                  display: "block",
                                  fontSize: "0.74rem",
                                  lineHeight: 1.35,
                                }}
                              >
                                {name}
                                {deadline ? ` · ${deadline}` : ""}
                              </Typography>
                            </Box>

                            <Tooltip
                              title={t("myDashboard.rfqOpenEstimate", {
                                defaultValue: "Abrir estimate",
                              })}
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={editing}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEstimate(item);
                                  }}
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    color: accent,
                                    bgcolor: alpha(accent, 0.08),
                                    "&:hover": {
                                      bgcolor: alpha(accent, 0.16),
                                    },
                                  }}
                                >
                                  <ArrowForwardIosRoundedIcon
                                    sx={{ fontSize: 13 }}
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
